import isEmpty from 'lodash/isEmpty';
import { Dropdown, DropdownItem } from '../../dropdown';
import { ModalHeader, Headline, MenuItem } from './styles';
import { useCallback, useMemo, useState } from 'react';
import { captureException } from '../../../services/logService';
import { rootStore } from '../../../mobx/rootStore';
import { ProjectModel } from '../../../mobx/models/project';
import { ProjectTemplateModel } from '../../../mobx/models/projectTemplate';
import { TASK_PRIORITY } from '../../../global/constants';

interface Props {
  isTemplateProject?: boolean;
}

const RenderProjectList = (props: Props) => {
  const { isTemplateProject } = props;
  const { projectStore, taskStore, projectTemplateStore } = rootStore;
  const { projects: list } = projectStore;
  const { createTaskInput } = taskStore;
  const { project: currentProject } = createTaskInput;
  const { projectTemplates: projectTemplateList } = projectTemplateStore;

  // other variables
  const menuItems = useMemo(() => {
    const projectList = isTemplateProject ? projectTemplateList : list;
    return projectList?.map((item) => ({
      label: item?.name,
      value: item?.id,
      name: item?.name,
      key: item?.id
    }));
  }, [list, isTemplateProject, projectTemplateList]);

  // State for project search query
  const [query, setQuery] = useState('');

  // Apply search filter dynamically
  const searchResult = useMemo(() => {
    return menuItems?.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));
  }, [query, menuItems]);

  // Handle search input change
  const onChangeSearch = useCallback((value: string) => {
    setQuery(value);
  }, []);

  //   for update project value
  const onUpdateProjectValue = useCallback(
    async (project?: ProjectModel | ProjectTemplateModel) => {
      if (project?.id !== currentProject?.id) {
        try {
          if (isTemplateProject) {
            rootStore.taskStore.createTaskInput.setProject(project?.id || '', true);
          } else {
            if (!project?.id) return;
            rootStore.taskStore.createTaskInput.setProject(project?.id);
            // Parent task belongs to the previous project
            rootStore.taskStore.createTaskInput.setParentTaskId('');
            if (project?.statusEnable) {
              const defaultStatus = project?.statusData?.find((status) => status?.Default);
              rootStore.taskStore.createTaskInput.setStatusId(defaultStatus?.['_id'] || '');
            }
            if (project?.isGroupEnabled) {
              rootStore.taskStore.createTaskInput.setGroupId(project?.taskGroups[0]?.id || '');
            }
            if (project?.priority?.enabled) {
              rootStore.taskStore.createTaskInput.setPriority(project?.priority?.default || 0);
            } else {
              rootStore.taskStore.createTaskInput.setPriority(TASK_PRIORITY.NO_PRIORITY);
            }
          }
        } catch (e) {
          captureException(e);
        }
      }
    },
    [currentProject?.id, isTemplateProject]
  );

  // Handle dropdown click
  const handleDropdownClick = useCallback(
    (key: string | number) => {
      const selectedProject = list?.find((item) => item?.id === String(key));
      onUpdateProjectValue(selectedProject || currentProject);
    },
    [list, onUpdateProjectValue, currentProject]
  );

  //   render search data
  const renderSearchData = useMemo(() => {
    return searchResult?.map((projectList) => (
      <DropdownItem
        key={projectList?.key}
        label={projectList?.label}
        onClick={() => handleDropdownClick(projectList?.key)}
        isSelected={projectList?.key === currentProject?.id} // Mark selected project
      />
    ));
  }, [handleDropdownClick, searchResult, currentProject]);

  return (
    <ModalHeader>
      <Dropdown
        modalTitle='Project'
        isMinWidth={148}
        onChangeSearch={onChangeSearch}
        content={
          <Headline>
            {!isEmpty(currentProject?.name)
              ? currentProject?.name
              : !isEmpty(createTaskInput?.project?.name)
              ? createTaskInput?.project?.name
              : 'Select Project'}
          </Headline>
        }
        trigger='click'>
        <MenuItem>{renderSearchData}</MenuItem>
      </Dropdown>
    </ModalHeader>
  );
};

export default RenderProjectList;
